import {Component, ElementRef, forwardRef, Injector, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule
} from '@angular/forms';
import {MatFormField, MatFormFieldControl, MatLabel, MatPrefix, MatSuffix} from '@angular/material/form-field';
import {MatSelect, MatSelectModule} from '@angular/material/select';
import {MatOptgroup, MatOption} from '@angular/material/core';
import {MatIcon} from '@angular/material/icon';
import {MatCheckbox, MatCheckboxChange} from '@angular/material/checkbox';
import {MatInput} from '@angular/material/input';
import {MatIconButton} from '@angular/material/button';
import {FlexModule} from '@angular/flex-layout';
import {LoadingService} from '@ui-kit/framework/util/service/loading.service';
import {IndustryCategory, SelectableCategory} from "@features/settings/industries/industry.model";
import {ManageIndustriesService} from "@features/settings/industries/manage-industries.service";
import {lastValueFrom, Subject} from 'rxjs';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {FocusMonitor} from '@angular/cdk/a11y';

@Component({
  selector: 'app-industry-select',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatSelectModule,
    MatOption,
    MatOptgroup,
    MatIcon,
    MatCheckbox,
    MatInput,
    MatPrefix,
    MatSuffix,
    MatIconButton,
    FlexModule
  ],
  templateUrl: './industry-select.component.html',
  styleUrls: ['./industry-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IndustrySelectComponent),
      multi: true
    },
    {
      provide: MatFormFieldControl,
      useExisting: IndustrySelectComponent
    }
  ]
})
export class IndustrySelectComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string[]> {
  static nextId = 0;

  @ViewChild(MatSelect, {static: true}) matSelect!: MatSelect;
  @Input() offeringUuid!: string | undefined;
  @Input("multiple") allowMultiple: boolean = true;
  @Input() showAllOptions: boolean = false;

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string = 'Select Industries';

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): string[] {
    return this.selectedIndustryCodes;
  }
  set value(val: string[]) {
    this.selectedIndustryCodes = val || [];
    this.stateChanges.next();
  }

  allIndustries: SelectableCategory[] = [];
  filteredIndustries: SelectableCategory[] = [];
  selectedIndustryCodes: string[] = [];
  searchControl = new FormControl('');

  stateChanges = new Subject<void>();
  id = `industry-select-${IndustrySelectComponent.nextId++}`;
  focused = false;
  touched = false;
  controlType = 'industry-select';
  autofilled?: boolean;

  get empty(): boolean {
    return !this.selectedIndustryCodes || this.selectedIndustryCodes.length === 0;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  get errorState(): boolean {
    return this.touched && this.empty && this.required;
  }

  // Control value accessor
  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngControl: NgControl | null = null;

  constructor(
    private industriesService: ManageIndustriesService,
    private loader: LoadingService,
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private injector: Injector
  ) {
    setTimeout(() => {
      this.ngControl = this.injector.get(NgControl, null);
    });
  }

  ngOnInit() {
    this.loadIndustries();

    // Setup search
    this.searchControl.valueChanges.subscribe(searchTerm => {
      this.filterIndustries(searchTerm || '');
    });
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  onFocusIn() {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
    this.focused = false;
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement = this._elementRef.nativeElement.querySelector('.industry-select-container');
    controlElement?.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(): void {
    this.matSelect.open();
  }

  private loadIndustries() {
    this.loader.show();

    if (this.showAllOptions) {
      this.industriesService.getAllSelectableOptionsForOffering().subscribe({
        next: (categories) => {
          this.allIndustries = categories;
          this.filteredIndustries = [...categories];
          this.loader.hide();
        },
        error: (error) => {
          console.error('Error loading industries:', error);
          this.loader.hide();
        }
      });
    } else {
      lastValueFrom(this.industriesService.getAllEnabledIndustries())
        .then((res: IndustryCategory[]) => {
          this.allIndustries = res.map(category => ({
            ...category,
            industries: (category.industries ?? []).map(ind => ({
              ...ind,
              selected: true,
            })),
            selected: true
          }));

          this.filteredIndustries = [...this.allIndustries];
        })
        .catch((error) => {
          console.error('Error loading industries:', error);
        })
        .finally(() => {
          this.loader.hide();
        })
    }
  }

  private filterIndustries(searchTerm: string) {
    if (!searchTerm.trim()) {
      this.filteredIndustries = [...this.allIndustries];
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredIndustries = this.allIndustries
      .map(category => ({
        ...category,
        industries: category.industries?.filter(industry =>
          industry.name.toLowerCase().includes(term) ||
          industry.description?.toLowerCase().includes(term)
        ) || []
      }))
      .filter(category =>
        category.name.toLowerCase().includes(term) ||
        category.description?.toLowerCase().includes(term) ||
        (category.industries && category.industries.length > 0)
      );
  }

  isCategoryFullySelected(category: IndustryCategory): boolean {
    if (!category.industries || category.industries.length === 0) {
      return false;
    }
    return category.industries.every(ind =>
      this.selectedIndustryCodes.includes(ind.code)
    );
  }

  isCategoryPartiallySelected(category: IndustryCategory): boolean {
    if (!category.industries || category.industries.length === 0) {
      return false;
    }
    const selectedCount = category.industries.filter(ind =>
      this.selectedIndustryCodes.includes(ind.code)
    ).length;
    return selectedCount > 0 && selectedCount < category.industries.length;
  }

  toggleCategory(category: IndustryCategory, event: Event | MatCheckboxChange) {
    if (event instanceof Event && this.allowMultiple) {
      event.stopPropagation();
    }

    if (!category.industries || category.industries.length === 0) {
      return;
    }

    const isFullySelected = this.isCategoryFullySelected(category);

    if (isFullySelected) {
      this.selectedIndustryCodes = this.selectedIndustryCodes.filter(code =>
        !category.industries!.some(ind => ind.code === code)
      );
    } else {
      const categoryIndustryCodes = category.industries.map(ind => ind.code);
      this.selectedIndustryCodes = [
        ...this.selectedIndustryCodes.filter(code =>
          !categoryIndustryCodes.includes(code)
        ),
        ...categoryIndustryCodes
      ];
    }

    this.onChange(this.selectedIndustryCodes);
    this.onTouched();
    this.stateChanges.next();
  }

  onIndustrySelectionChange() {
    this.onChange(this.selectedIndustryCodes);
    this.onTouched();
    this.stateChanges.next();
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  writeValue(value: string[]): void {
    this.selectedIndustryCodes = value || [];
    this.stateChanges.next();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
    this.stateChanges.next();
  }
}
